// Load matching tab (ST-00218 P2): the route table — per route the current figures (BF / CF /
// variance), the new ones (BF / CF / missing / receipts with no dispatch / variance), the
// change, how the loads were matched (tier mix), data quality, sign-off state and the review
// action. Routes without receipt data read "n/a — no receipt data"; a misconfigured route
// grouping shows the configuration hint only (Q6: the customer edits the grouping).
// Phones get one card per route instead of the wide table.
import { Fragment } from 'react';
import { FaChevronRight, FaTools } from 'react-icons/fa';
import type { CompareRouteRow, ConfigHint } from '../../../../types/loadMatching';
import { DATA_STATUS_TEXT, MATCH_MODE_LABELS, fmtN, fmtPct, fmtPts, isNa } from './format';
import { DqBadge, HintText, SignoffBadge, TierMixBar } from './parts';

export interface RouteTableProps {
  routes: CompareRouteRow[];
  checked: ReadonlySet<number>;
  onToggle: (groupingId: number) => void;
  onToggleAll: (ids: number[], on: boolean) => void;
  onOpen: (groupingId: number) => void;
  phone: boolean;
}

const DATA_KINDS: ReadonlyArray<ConfigHint['kind']> = ['no_receipt_data', 'no_dispatch_data'];

/** The n/a line of a route: "n/a — no receipt data from AgriPro" (or the plain status text). */
export function naLine(row: CompareRouteRow): string | null {
  if (!isNa(row.new)) return null;
  const dataHint = row.hints.find((h) => DATA_KINDS.includes(h.kind));
  if (dataHint) {
    const t = dataHint.text;
    return `n/a — ${t.charAt(0).toLowerCase()}${t.slice(1)}`;
  }
  return DATA_STATUS_TEXT[row.new.dataStatus] || 'n/a';
}

/** Configuration hints of a route (received off-route, rules) — the data-status ones excluded. */
export const configHints = (row: CompareRouteRow): ConfigHint[] =>
  row.hints.filter((h) => !DATA_KINDS.includes(h.kind));

export function RouteNotes({ row }: { row: CompareRouteRow }) {
  const na = naLine(row);
  const hints = configHints(row);
  if (!na && !hints.length) return null;
  return (
    <div className="mt-1 space-y-0.5">
      {na && <p className="text-[11px] font-medium text-amber-800">{na}</p>}
      {hints.map((h, i) => (
        <p key={i} className="text-[11px] text-amber-800 flex items-start gap-1">
          <FaTools className="w-2.5 h-2.5 mt-[3px] flex-shrink-0" aria-hidden="true" />
          <span>
            <HintText text={h.text} />
            <span className="text-slate-500"> — change the route grouping in Settings.</span>
          </span>
        </p>
      ))}
    </div>
  );
}

/**
 * The route's Load matching setting (ST-00218 P3): "uses Truck + time", or the delivery-number
 * mode it uses ("ticket matching exact").
 */
const modeLine = (row: CompareRouteRow): string =>
  row.loadMatching === 'engine'
    ? 'uses Truck + time'
    : `ticket matching ${MATCH_MODE_LABELS[row.old.matchMode]}`;

const na = (row: CompareRouteRow, v: string): string => (isNa(row.new) ? 'n/a' : v);
/** Loads that need review (engine 1.1.0; '—' for a 1.0.0 answer without the field). */
const reviewN = (row: CompareRouteRow): string =>
  typeof row.new.needsReviewN === 'number' ? na(row, fmtN(row.new.needsReviewN)) : '—';
/** The answer carries needs-review figures (engine 1.1.0): show the column. */
const hasReview = (rows: CompareRouteRow[]): boolean =>
  rows.some((r) => typeof r.new.needsReviewN === 'number');

const th =
  'px-2 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap';
const td = 'px-2 py-2.5 text-xs text-slate-800 whitespace-nowrap tabular-nums text-right align-top';

export default function RouteTable({
  routes,
  checked,
  onToggle,
  onToggleAll,
  onOpen,
  phone,
}: RouteTableProps) {
  const ids = routes.map((r) => r.groupingId);
  const allOn = ids.length > 0 && ids.every((id) => checked.has(id));
  const review = hasReview(routes);

  if (phone) {
    return (
      <ul className="space-y-2" aria-label="Routes">
        {routes.map((r) => (
          <li
            key={r.groupingId}
            className="bg-surface rounded-lg border border-slate-200 px-3 py-3"
            data-testid={`route-card-${r.groupingId}`}
          >
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 accent-primary-600"
                checked={checked.has(r.groupingId)}
                onChange={() => onToggle(r.groupingId)}
                aria-label={`Select ${r.name}`}
              />
              <div className="min-w-0 flex-1">
                <button
                  type="button"
                  onClick={() => onOpen(r.groupingId)}
                  className="text-left text-sm font-semibold text-slate-900 hover:text-primary-700"
                >
                  {r.name}
                </button>
                <p className="text-[11px] text-slate-500">{modeLine(r)}</p>
                <RouteNotes row={r} />
              </div>
            </div>
            <dl className="mt-2 grid grid-cols-[auto_1fr_1fr] gap-x-3 gap-y-0.5 text-xs">
              <dt className="text-slate-500" />
              <dd className="text-[10px] uppercase tracking-wider text-slate-500 text-right">
                Now
              </dd>
              <dd className="text-[10px] uppercase tracking-wider text-slate-500 text-right">
                New
              </dd>
              <dt className="text-slate-500">Brought fwd</dt>
              <dd className="text-right tabular-nums">{fmtN(r.old.bfN)}</dd>
              <dd className="text-right tabular-nums font-semibold">{na(r, fmtN(r.new.bfN))}</dd>
              <dt className="text-slate-500">Carried fwd</dt>
              <dd className="text-right tabular-nums">{fmtN(r.old.cfN)}</dd>
              <dd className="text-right tabular-nums font-semibold">{na(r, fmtN(r.new.cfN))}</dd>
              <dt className="text-slate-500">Missing</dt>
              <dd className="text-right text-slate-400">—</dd>
              <dd className="text-right tabular-nums font-semibold">
                {na(r, fmtN(r.new.missingN))}
              </dd>
              {typeof r.new.needsReviewN === 'number' && (
                <>
                  <dt className="text-slate-500">Needs review</dt>
                  <dd className="text-right text-slate-400">—</dd>
                  <dd className="text-right tabular-nums font-semibold">{reviewN(r)}</dd>
                </>
              )}
              <dt className="text-slate-500">No dispatch</dt>
              <dd className="text-right text-slate-400">—</dd>
              <dd className="text-right tabular-nums font-semibold">{fmtN(r.new.orphanN)}</dd>
              <dt className="text-slate-500">Variance</dt>
              <dd className="text-right tabular-nums">{fmtPct(r.old.variancePct)}</dd>
              <dd className="text-right tabular-nums font-semibold">{fmtPct(r.new.variancePct)}</dd>
            </dl>
            <div className="mt-2">
              <TierMixBar mix={r.tierMix} />
            </div>
            <div className="mt-2 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <DqBadge indicator={r.dq.indicator} />
                <SignoffBadge state={r.signoff.state} />
              </div>
              <button
                type="button"
                onClick={() => onOpen(r.groupingId)}
                className="inline-flex items-center gap-1 h-8 px-3 text-xs font-medium rounded-md border border-slate-300 text-slate-700"
              >
                Review <FaChevronRight className="w-2.5 h-2.5" />
              </button>
            </div>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="bg-surface rounded-lg border border-slate-200 overflow-x-auto">
      <table className="w-full border-collapse" aria-label="Routes: current vs new matching">
        <thead className="bg-slate-50">
          <tr className="border-b border-slate-200">
            <th className="px-2" />
            <th className="px-2" />
            <th
              colSpan={3}
              className="px-2 pt-2 text-[10px] font-semibold uppercase tracking-widest text-slate-500 text-center border-l border-slate-200"
            >
              Now
            </th>
            <th
              colSpan={review ? 6 : 5}
              className="px-2 pt-2 text-[10px] font-semibold uppercase tracking-widest text-primary-700 text-center border-l border-slate-200"
            >
              New matching
            </th>
            <th colSpan={5} className="border-l border-slate-200" />
          </tr>
          <tr className="border-b border-slate-200">
            <th className="px-2 py-2 w-8">
              <input
                type="checkbox"
                className="h-4 w-4 accent-primary-600"
                checked={allOn}
                onChange={() => onToggleAll(ids, !allOn)}
                aria-label="Select all routes"
              />
            </th>
            <th className={`${th} text-left`}>Route</th>
            <th
              className={`${th} text-right border-l border-slate-200`}
              title="Brought forward (loads)"
            >
              BF
            </th>
            <th className={`${th} text-right`} title="Carried forward (loads)">
              CF
            </th>
            <th className={`${th} text-right`}>Var</th>
            <th
              className={`${th} text-right border-l border-slate-200`}
              title="Brought forward (loads)"
            >
              BF
            </th>
            <th className={`${th} text-right`} title="Carried forward (loads)">
              CF
            </th>
            <th className={`${th} text-right`} title="Missing loads">
              Missing
            </th>
            {review && (
              <th
                className={`${th} text-right`}
                title="Loads that need review: the truck was dispatched again before a receipt"
              >
                Review
              </th>
            )}
            <th className={`${th} text-right`} title="Receipts with no matching dispatch">
              No disp.
            </th>
            <th className={`${th} text-right`}>Var</th>
            <th className={`${th} text-right border-l border-slate-200`} title="Change in variance">
              Δ var
            </th>
            <th
              className={`${th} text-left`}
              title="How the matched loads were matched (tiers 1–4)"
            >
              Matched by
            </th>
            <th className={`${th} text-left`}>Data quality</th>
            <th className={`${th} text-left`}>Sign-off</th>
            <th className="px-2" />
          </tr>
        </thead>
        <tbody>
          {routes.map((r) => {
            const sel = checked.has(r.groupingId);
            const notes = naLine(r) !== null || configHints(r).length > 0;
            const rowBg = sel ? 'bg-primary-50/40' : '';
            return (
              <Fragment key={r.groupingId}>
                <tr
                  data-testid={`route-row-${r.groupingId}`}
                  className={`${notes ? '' : 'border-b border-slate-100'} ${rowBg || 'hover:bg-slate-50'}`}
                >
                  <td className="px-2 py-2.5 align-top">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-primary-600 mt-0.5"
                      checked={sel}
                      onChange={() => onToggle(r.groupingId)}
                      aria-label={`Select ${r.name}`}
                    />
                  </td>
                  <td className="px-2 py-2.5 align-top min-w-[14rem] max-w-[22rem]">
                    <button
                      type="button"
                      onClick={() => onOpen(r.groupingId)}
                      className="text-left text-xs font-semibold text-slate-900 hover:text-primary-700"
                    >
                      {r.name}
                    </button>
                    <p className="text-[10px] text-slate-500">{modeLine(r)}</p>
                  </td>
                  <td className={`${td} border-l border-slate-100 text-slate-600`}>
                    {fmtN(r.old.bfN)}
                  </td>
                  <td className={`${td} text-slate-600`}>{fmtN(r.old.cfN)}</td>
                  <td className={`${td} text-slate-600`}>{fmtPct(r.old.variancePct)}</td>
                  <td className={`${td} border-l border-slate-100 font-semibold`}>
                    {na(r, fmtN(r.new.bfN))}
                  </td>
                  <td className={`${td} font-semibold`}>{na(r, fmtN(r.new.cfN))}</td>
                  <td className={`${td} font-semibold`}>{na(r, fmtN(r.new.missingN))}</td>
                  {review && (
                    <td
                      className={`${td} font-semibold`}
                      data-testid={`route-review-${r.groupingId}`}
                    >
                      {reviewN(r)}
                    </td>
                  )}
                  <td className={`${td} font-semibold`}>{fmtN(r.new.orphanN)}</td>
                  <td className={`${td} font-semibold`}>{fmtPct(r.new.variancePct)}</td>
                  <td className={`${td} border-l border-slate-100`}>
                    {fmtPts(r.delta.variancePctPoints)}
                  </td>
                  <td className="px-2 py-3 align-top w-28">
                    <TierMixBar mix={r.tierMix} className="w-24" />
                  </td>
                  <td className="px-2 py-2.5 align-top whitespace-nowrap">
                    <DqBadge indicator={r.dq.indicator} />
                  </td>
                  <td className="px-2 py-2.5 align-top">
                    <SignoffBadge state={r.signoff.state} />
                  </td>
                  <td className="px-2 py-2 align-top text-right">
                    <button
                      type="button"
                      onClick={() => onOpen(r.groupingId)}
                      aria-label={`Review ${r.name}`}
                      className="inline-flex items-center gap-1 h-7 px-2.5 text-[11px] font-medium rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
                    >
                      Review <FaChevronRight className="w-2 h-2" />
                    </button>
                  </td>
                </tr>
                {notes && (
                  <tr
                    data-testid={`route-notes-${r.groupingId}`}
                    className={`border-b border-slate-100 ${rowBg}`}
                  >
                    <td />
                    <td colSpan={review ? 15 : 14} className="px-2 pb-2.5 -mt-1">
                      <RouteNotes row={r} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
